// DimCL (Dim Cricket League "(e)" rounds) — reference admin rules panel
// (`.bb-rules` icon on /admin/tpVirtual/…), ported verbatim 2026-10-19.
export default function DimCLRules() {
  return (
    <div className="rules-section">
            <div>
                <h6 className="rules-highlight">DimCL Market Settlement Rules</h6>
                <p>This document outlines the settlement rules for the DimCL market types defined in the system.</p>
            </div>
            <div>
                <h6 className="rules-highlight">General Rules</h6>
                <ul className="pl-4 pr-4">
                    <li>1. <b>Official Results:</b> All bets are settled based on the official score data generated by the AI simulation engine.</li>
                    <li>2. <b>Match Completion:</b> Markets are settled only when the match (or the specific condition, e.g., "Inning") is completed.</li>
                    <li>3. <b>Void Matches:</b> If a match is aborted due to system failure or technical issues, all unsettled bets are void.</li>
                </ul>
            </div>
            <br />
            <div>
                <h6 className="rules-highlight">Scoring Mechanics</h6>
                <p>The game engine determines run scoring through a sophisticated physics and statistics-based simulation:</p>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Simulation Factors:</b> Outcomes are calculated based on player attributes, including <b>batsman strength</b> and <b>bowler spin/pace skills</b>.</li>
                    <li><b>Physics Engine:</b> These attributes determine the <b>direction</b> and <b>speed</b> of the ball off the bat.</li>
                    <li><b>Run Determination:</b> The engine calculates whether a shot results in a Boundary (4 or 6) or running between wickets (1 or 2 runs) by simulating how the ball would travel in a real cricket scenario given the same velocity and trajectory. Wide ball scores team 2 runs. NoBall outcomes are not supported by engine.</li>
                </ul>
            </div>
            <br />
            <div>
                <h6 className="rules-highlight">Market Definitions &amp; Settlement</h6>
                <h6 className="rules-highlight">Match Winner Markets</h6>
                <h6 className="rules-sub-highlight">1. 2 Way Match Odds</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Description:</b> Prediction of the winner of the match (Home or Away).</li>
                    <li><b>Settlement:</b> Settled on the official match winner.</li>
                    <li><b>Tie Rule:</b> If the match ends in a tie, bets on this market are settled as <b>DeadHeat</b>.</li>
                </ul>
                <h6 className="rules-sub-highlight">2. 3 Way Match Odds</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Description:</b> Prediction of the match outcome: Home Win, Away Win, or Draw.</li>
                    <li><b>Settlement:</b> Settled on the result at the end of the scheduled overs.</li>
                    <li><b>Tie:</b> The "Draw" selection is the winner if scores are level. Home and Away selections are losers.</li>
                </ul>
                <h6 className="rules-sub-highlight">3. Toss Winner</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Description:</b> Prediction of which team will win the pre-match coin toss.</li>
                    <li><b>Settlement:</b> Settled based on the result of the virtual coin toss event.</li>
                </ul>
            </div>
            <br />
            <div>
                <h6 className="rules-highlight">Run Markets</h6>
                <h6 className="rules-sub-highlight">4. Inning Runs</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Type:</b> Over/Under</li>
                    <li><b>Description:</b> Total number of runs scored by a specific team in their innings.</li>
                    <li><b>Settlement:</b> Includes all runs scored by batsmen and extras - Wides and No Balls.</li>
                </ul>
                <h6 className="rules-sub-highlight">5. Match Total Runs</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Type:</b> Over/Under</li>
                    <li><b>Description:</b> The aggregate sum of runs scored by <b>both</b> teams in the match.</li>
                    <li><b>Settlement:</b> Team A Score + Team B Score.</li>
                </ul>
                <h6 className="rules-sub-highlight">6. Team Odd/Even</h6>
                <ul>
                    <li><b>Description:</b> Prediction of whether a team's final score will be an Odd or Even number.</li>
                    <li>
                        <b>Settlement:</b>
                        <ul className="pl-4 pr-4 list-style">
                            <li>Odd: 1, 3, 5, 7...</li>
                            <li>Even: 0, 2, 4, 6...</li>
                        </ul>
                    </li>
                </ul>
                <h6 className="rules-sub-highlight">7. Match Odd/Even</h6>
                <ul className="pr-4 pl-4 list-style">
                    <li><b>Description:</b> Prediction of whether the match total (Team A + Team B) is Odd or Even.</li>
                    <li><b>Settlement:</b> Same rules as Team Odd/Even.</li>
                </ul>
            </div>
            <br />
            <div>
                <h6 className="rules-highlight">Boundary Markets (4s &amp; 6s)</h6>
                <h6 className="rules-sub-highlight">8. Team 4s</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Type:</b> Over/Under</li>
                    <li><b>Settlement:</b> Counts only shots that cross the boundary rope and are awarded 4 runs.</li>
                </ul>
                <h6 className="rules-sub-highlight">9. Team 6s</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Type:</b> Over/Under</li>
                    <li><b>Description:</b> Total number of Sixes (6s) hit by a specific team.</li>
                    <li><b>Settlement:</b> Counts shots that clear the boundary rope and are awarded 6 runs.</li>
                </ul>
                <h6 className="rules-sub-highlight">10. Total 4s</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Type:</b> Over/Under</li>
                    <li><b>Description:</b> Total number of Fours hit by both teams combined.</li>
                </ul>
                <h6 className="rules-sub-highlight">11. Total 6s</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Type:</b> Over/Under</li>
                    <li><b>Description:</b> Total number of Sixes hit by both teams combined.</li>
                </ul>
            </div>
            <br />
            <div>
                <h6 className="rules-highlight">Wicket Markets</h6>
                <h6 className="rules-sub-highlight">12. Match Total Wickets</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Type:</b> Over/Under</li>
                    <li><b>Description:</b> Total number of wickets lost by both teams in the match.</li>
                    <li><b>Settlement:</b> Team A Wickets + Team B Wickets.</li>
                </ul>
            </div>
            <br />
            <div>
                <h6 className="rules-highlight">Delivery Markets</h6>
                <h6 className="rules-sub-highlight">13. Next Ball</h6>
                <ul className="pl-4 pr-4 list-style">
                    <li><b>Description:</b> The outcome of the specific next delivery.</li>
                    <li><b>Selections:</b> 0, 1, 2, 3, 4, 6, Wicket, Wide.</li>
                    <li>
                        <b>Settlement Rules:</b>
                        <ul className="pl-4 pr-4 list-style">
                            <li><b>Run outcomes</b> (0, 1, 2, 3, 4, 6): Settled based on runs attributed to the batsman.</li>
                            <li><b>Extras:</b> If the ball is a Wide, the market settles as "Wide. AI game engine does not support "No Ball" outcomes.</li>
                            <li><b>Wicket:</b> If a wicket falls, "Wicket" is the winning selection.</li>
                        </ul>
                    </li>
                </ul>
            </div>
    <p><b>When a wide ball is shown during the live stream, two runs are added to the scorecard, but it is not recorded as a wide. The ball should also be counted as part of the over.</b></p>
        </div>
  )
}
